import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Extension } from '@tiptap/core';
import { styled } from '@mui/material/styles';
import logger from '@widgets/logger';
import { SplitModal, PreviewWrapper } from '@components/SplitModal';
import SplitButton from '@components/SplitButton';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { Typography } from '@components/Typography';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import Alert from '@components/Toaster/Alert';
import TextEditor from '@components/TextEditor';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { useTranslation } from '@hooks';
import { alertActions, campaignTemplatesActions } from '@actions';
import { campaignsService, campaignTemplatesService } from '@services';
import { handleApiError } from '@helpers/api';
import { validateFileSize } from '@helpers/upload-file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { MB } from '@constants/file';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { AccIntegrationName } from '@constants/integrations';
import { getActiveIntegrations } from '@helpers/integrations';
import {
    TEMPLATE_CHANNEL_FILTER,
    WA_HEADER_FORMAT,
    WA_LIMITS,
    WA_TEMPLATE_CATEGORIES,
    WA_TEMPLATE_STATUS,
} from '@constants/campaignTemplates';
import BubblePreview from './BubblePreview';
import ButtonsEditor from './ButtonsEditor';
import VariablesTable from './VariablesTable';
import { TwoColumnsRow } from './styles';
import {
    buildComponents,
    buildWriteModel,
    createEmptyDraft,
    defaultWaLanguage,
    displayToWaTokens,
    isContentUnchanged,
    lintDraft,
    lintSetupOnly,
    matchPlaceholders,
    parseTemplate,
    slugifyTemplateName,
    syncMappings,
    waLanguagesForLocale,
    waToDisplayTokens,
} from './waTemplateUtils';

const waContentLength = value => displayToWaTokens(value).length;

const singleLineExtension = Extension.create({
    name: 'singleLine',
    addKeyboardShortcuts() {
        return {
            Enter: () => true,
            'Shift-Enter': () => true,
        };
    },
});

const collapseNewlines = value => value.replace(/\s*\n+\s*/g, ' ');

const FormColumn = styled('div')({
    minWidth: 0,
    overflowY: 'auto',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    '@media (max-width: 768px)': {
        overflowY: 'initial',
    },
});

const PhoneScreen = styled('div')({
    height: '100%',
    background: '#E5DDD5',
    padding: '24px 12px',
    overflowY: 'auto',
});

const PreviewColumn = styled(PreviewWrapper)({
    padding: 24,
    '@media (max-width: 768px)': {
        display: 'none',
    },
});

const HeaderFormatGroup = styled(RadioButtonsGroup)({
    '&&': {
        width: 'fit-content',
    },
    '& .MuiFormControlLabel-root': {
        flex: '0 0 auto',
        padding: '8px 24px',
    },
});

const Banner = ({ children }) => (
    <Alert severity="warning">
        <Typography variant="body2" sx={{ color: 'inherit' }}>
            {children}
        </Typography>
    </Alert>
);

const SectionTitle = styled(Typography)({
    marginTop: 8,
});

const LANGUAGE_LABELS = {
    en: 'English',
    en_US: 'English (US)',
    en_GB: 'English (UK)',
    es: 'Español',
    es_MX: 'Español (México)',
    pt_BR: 'Português (Brasil)',
    pt_PT: 'Português (Portugal)',
    uk: 'Українська',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
};

const EDITABLE_NON_DRAFT_STATUSES = [
    WA_TEMPLATE_STATUS.APPROVED,
    WA_TEMPLATE_STATUS.REJECTED,
    WA_TEMPLATE_STATUS.PAUSED,
];

const WhatsAppTemplateBuilder = ({ isOpen, onClose, template, isView, onSaved }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const account = useSelector(state => state.account?.account);
    const { data: templates } = useSelector(state => state.campaignTemplates);

    const [draft, setDraft] = useState(createEmptyDraft);
    const [errors, setErrors] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [persistedTemplateId, setPersistedTemplateId] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setPersistedTemplateId(template?.id || null);
        // syncMappings also derives rows for variables the stored mappings don't cover yet —
        // synced external templates arrive with unknown params unmapped ("needs setup").
        const parsed = template
            ? parseTemplate(template)
            : { ...createEmptyDraft(), language: defaultWaLanguage(account?.locale) };
        setDraft({ ...parsed, mappings: syncMappings(parsed) });
        setErrors({});
        setIsDirty(false);
    }, [isOpen, template, account?.locale]);

    const status = template?.status || WA_TEMPLATE_STATUS.DRAFT;
    const isDraftStatus = status === WA_TEMPLATE_STATUS.DRAFT;
    // Content edits re-enter Meta review and mapping/header edits change what live sends say —
    // the backend rejects both until those campaigns are repointed or deleted.
    const isLockedByCampaigns = !isDraftStatus && (template?.usedByScheduledOrRunningCampaignsCount || 0) > 0;
    const setupEditable =
        !isView && (isDraftStatus || EDITABLE_NON_DRAFT_STATUSES.includes(status)) && !isLockedByCampaigns;
    const contentEditable = setupEditable;
    const identityEditable = !isView && isDraftStatus;
    const canSubmit = !isView && (isDraftStatus || status === WA_TEMPLATE_STATUS.REJECTED);

    const wabaOptions = useMemo(() => {
        const wabas = new Map();
        getActiveIntegrations(account, AccIntegrationName.WhatsApp).forEach(integration => {
            if (integration.userIdentifier) {
                wabas.set(integration.userIdentifier, integration.accountLabel || integration.userIdentifier);
            }
        });
        (templates || []).forEach(item => {
            if (item.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP && item.waba && !wabas.has(item.waba)) {
                wabas.set(item.waba, item.waba);
            }
        });
        return [...wabas.entries()].map(([value, label]) => ({ value, label }));
    }, [account, templates]);

    const languageOptions = useMemo(() => {
        const codes = waLanguagesForLocale(account?.locale);
        // Synced external templates can carry a language outside the curated list — keep it visible.
        if (draft.language && !codes.includes(draft.language)) {
            codes.unshift(draft.language);
        }
        return codes.map(code => ({ value: code, label: LANGUAGE_LABELS[code] || code }));
    }, [account?.locale, draft.language]);

    const previewComponents = useMemo(() => buildComponents(draft), [draft]);

    const patchContent = patch => {
        setDraft(current => {
            const next = { ...current, ...patch };
            return { ...next, mappings: syncMappings(next) };
        });
        setErrors({});
        setIsDirty(true);
    };

    const patchIdentity = patch => {
        setDraft(current => ({ ...current, ...patch }));
        setErrors({});
        setIsDirty(true);
    };

    const uploadHeaderImage = async e => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 5 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await campaignsService.uploadEmailCampaignImage(data);
            patchContent({ headerMediaUrl: response.publicUrl });
        } catch (_) {
            logger.error("Couldn't upload template header image");
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    // Content-unchanged saves of submitted templates go through the mappings endpoint: it
    // skips creation lint (externally authored content may legally violate it) and never
    // re-enters Meta review just to finish variable/header setup.
    const isSetupOnlySave = () =>
        !!persistedTemplateId && !!template && !isDraftStatus && isContentUnchanged(template, draft);

    const runLint = () => {
        const lintErrors = isSetupOnlySave() ? lintSetupOnly(draft, t) : lintDraft(draft, t);
        if (wabaOptions.length > 1 && !draft.waba && !persistedTemplateId) {
            lintErrors.waba = t('TemplateLibrary.waWabaRequired');
        }
        setErrors(lintErrors);
        return Object.keys(lintErrors).length === 0;
    };

    const saveTemplate = async () => {
        const model = buildWriteModel(draft);
        const saved = isSetupOnlySave()
            ? await campaignTemplatesService.updateWhatsAppTemplateMappings(persistedTemplateId, {
                  variableMappings: model.variableMappings,
                  headerMediaUrl: model.headerMediaUrl,
              })
            : persistedTemplateId
              ? await campaignTemplatesService.updateWhatsAppTemplate(persistedTemplateId, model)
              : await campaignTemplatesService.createWhatsAppTemplate(model);
        setPersistedTemplateId(saved.id);
        dispatch(campaignTemplatesActions.get());
        setIsDirty(false);
        return saved;
    };

    const onSave = async () => {
        if (!runLint()) return;
        try {
            setSaving(true);
            const saved = await saveTemplate();
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onSaved?.(saved);
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        } finally {
            setSaving(false);
        }
    };

    const onSubmitForReview = async () => {
        if (!runLint()) return;
        try {
            setSaving(true);
            const saved = await saveTemplate();
            // Saving an approved/paused template already sends it back to review (pending),
            // and the submit endpoint only accepts drafts and rejected templates.
            const needsSubmit =
                saved.status === WA_TEMPLATE_STATUS.DRAFT || saved.status === WA_TEMPLATE_STATUS.REJECTED;
            const submitted = needsSubmit ? await campaignTemplatesService.submitWhatsAppTemplate(saved.id) : saved;
            dispatch(campaignTemplatesActions.get());
            dispatch(alertActions.success(t('TemplateLibrary.waSubmitted')));
            onSaved?.(submitted);
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        } finally {
            setSaving(false);
        }
    };

    const headerVariableCount = matchPlaceholders(draft.headerText).length;

    const saveLabel = isDraftStatus ? t('TemplateLibrary.waSaveDraft') : t('TemplateLibrary.saveTemplate');

    return (
        <SplitModal
            isOpen={isOpen}
            handleClose={onClose}
            isDirty={isDirty}
            title={
                isView
                    ? t('TemplateLibrary.waViewTemplateTitle')
                    : persistedTemplateId
                      ? t('TemplateLibrary.waEditTemplateTitle')
                      : t('TemplateLibrary.waCreateTemplateTitle')
            }
            onPrimaryAction={setupEditable && !canSubmit ? onSave : null}
            primaryActionText={saveLabel}
            primaryActionDisabled={saving || (!isDirty && !!persistedTemplateId)}
            primaryButton={
                setupEditable && canSubmit ? (
                    <SplitButton
                        variant="contained"
                        disabled={saving}
                        options={[
                            { text: saveLabel, onClick: onSave },
                            { text: t('TemplateLibrary.waSubmitForReview'), onClick: onSubmitForReview },
                        ]}
                    />
                ) : null
            }
        >
            <FormColumn>
                {status === WA_TEMPLATE_STATUS.PENDING && <Banner>{t('TemplateLibrary.waPendingBanner')}</Banner>}
                {status === WA_TEMPLATE_STATUS.DISABLED && <Banner>{t('TemplateLibrary.waDisabledBanner')}</Banner>}
                {status === WA_TEMPLATE_STATUS.REJECTED && (
                    <Banner>
                        {t('TemplateLibrary.waRejectedBanner')}
                        {template?.rejectionReason ? ` ${template.rejectionReason}` : ''}
                    </Banner>
                )}
                {!isView && isLockedByCampaigns && <Banner>{t('TemplateLibrary.waLockedByCampaigns')}</Banner>}
                {!isView &&
                    !isLockedByCampaigns &&
                    (status === WA_TEMPLATE_STATUS.APPROVED || status === WA_TEMPLATE_STATUS.PAUSED) && (
                        <Banner>{t('TemplateLibrary.waResubmitBanner')}</Banner>
                    )}
                <Input
                    label={t('TemplateLibrary.templateName')}
                    required
                    fullWidth
                    disabled={!identityEditable}
                    error={!!errors.name}
                    helperText={errors.name || t('TemplateLibrary.waNameHint')}
                    inputProps={{
                        value: draft.name,
                        onChange: e => patchIdentity({ name: slugifyTemplateName(e.target.value) }),
                    }}
                />
                <TwoColumnsRow>
                    <Select
                        label={t('common.language')}
                        fullWidth
                        disabled={!identityEditable}
                        value={draft.language}
                        onChange={({ target: { value } }) => patchIdentity({ language: value })}
                        options={languageOptions}
                    />
                    <Select
                        label={t('TemplateLibrary.waCategory')}
                        fullWidth
                        disabled={!identityEditable}
                        value={draft.category}
                        onChange={({ target: { value } }) => patchIdentity({ category: value })}
                        options={WA_TEMPLATE_CATEGORIES.map(category => ({
                            value: category,
                            label: t(`TemplateLibrary.waCategory_${category}`),
                        }))}
                    />
                </TwoColumnsRow>
                {wabaOptions.length > 1 && (
                    <Select
                        label={t('TemplateLibrary.waWaba')}
                        fullWidth
                        disabled={!identityEditable || !!persistedTemplateId}
                        value={draft.waba}
                        onChange={({ target: { value } }) => patchIdentity({ waba: value })}
                        error={!!errors.waba}
                        helperText={errors.waba || null}
                        options={wabaOptions}
                    />
                )}

                <SectionTitle variant="subtitle2">{t('TemplateLibrary.waHeader')}</SectionTitle>
                <HeaderFormatGroup
                    id="pl-wa-header-format"
                    value={draft.headerFormat}
                    disabled={!contentEditable}
                    onChange={value => patchContent({ headerFormat: value, headerText: '', headerMediaUrl: '' })}
                    options={[
                        { value: WA_HEADER_FORMAT.NONE, label: t('TemplateLibrary.waHeaderNone') },
                        { value: WA_HEADER_FORMAT.TEXT, label: t('TemplateLibrary.waHeaderText') },
                        { value: WA_HEADER_FORMAT.IMAGE, label: t('TemplateLibrary.waHeaderImage') },
                    ]}
                />
                {draft.headerFormat === WA_HEADER_FORMAT.TEXT && (
                    <TextEditor
                        label={t('TemplateLibrary.waHeaderTextLabel')}
                        initialValue={waToDisplayTokens(draft.headerText)}
                        onChange={value =>
                            patchContent({
                                headerText: collapseNewlines(displayToWaTokens(parseTipTapJSONToPlainText(value))),
                            })
                        }
                        editable={contentEditable}
                        extraExtensions={[singleLineExtension]}
                        limit={WA_LIMITS.HEADER}
                        placeholderContext={PLACEHOLDER_CONTEXT.CAMPAIGN}
                        getLength={waContentLength}
                        error={!!errors.header}
                        helperText={errors.header || null}
                        toolbar={<PostDynamicVariables disabled={!contentEditable || headerVariableCount >= 1} />}
                    />
                )}
                {draft.headerFormat === WA_HEADER_FORMAT.IMAGE && (
                    <div>
                        <ImageUploaderSingle
                            extensions=".png, .jpg, .jpeg"
                            maxSize={5 * MB}
                            logoUrl={draft.headerMediaUrl}
                            disabled={uploading || !setupEditable}
                            onDelete={() => patchContent({ headerMediaUrl: '' })}
                            uploadLogo={uploadHeaderImage}
                            isUploading={uploading}
                        />
                        {errors.header ? (
                            <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                                {errors.header}
                            </Typography>
                        ) : null}
                    </div>
                )}

                <SectionTitle variant="subtitle2">{t('TemplateLibrary.waBody')}</SectionTitle>
                <TextEditor
                    label={t('common.message', { count: 1 })}
                    required
                    initialValue={waToDisplayTokens(draft.body)}
                    onChange={value => patchContent({ body: displayToWaTokens(parseTipTapJSONToPlainText(value)) })}
                    editable={contentEditable}
                    limit={WA_LIMITS.BODY}
                    placeholderContext={PLACEHOLDER_CONTEXT.CAMPAIGN}
                    getLength={waContentLength}
                    error={!!errors.body}
                    helperText={errors.body || null}
                    toolbar={<PostDynamicVariables disabled={!contentEditable} />}
                />

                <SectionTitle variant="subtitle2">{t('TemplateLibrary.waFooter')}</SectionTitle>
                <Input
                    label={t('TemplateLibrary.waFooterLabel')}
                    fullWidth
                    disabled={!contentEditable}
                    error={!!errors.footer}
                    helperText={errors.footer || null}
                    inputProps={{
                        value: draft.footer,
                        onChange: e => patchContent({ footer: e.target.value }),
                    }}
                />

                <SectionTitle variant="subtitle2">{t('TemplateLibrary.waButtons')}</SectionTitle>
                <ButtonsEditor
                    buttons={draft.buttons}
                    disabled={!contentEditable}
                    onChange={buttons => patchContent({ buttons })}
                />
                {errors.buttons ? (
                    <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                        {errors.buttons}
                    </Typography>
                ) : null}

                <VariablesTable
                    mappings={draft.mappings}
                    disabled={!setupEditable}
                    onChange={mappings => {
                        setDraft(current => ({ ...current, mappings }));
                        setErrors({});
                        setIsDirty(true);
                    }}
                />
                {errors.variables ? (
                    <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                        {errors.variables}
                    </Typography>
                ) : null}

                {canSubmit && (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('TemplateLibrary.waReviewNote')}
                    </Typography>
                )}
            </FormColumn>
            <PreviewColumn>
                <PhonePreviewFrame>
                    <PhoneScreen>
                        <BubblePreview
                            components={previewComponents}
                            headerMediaUrl={draft.headerMediaUrl}
                            mappings={draft.mappings}
                            substitute
                        />
                    </PhoneScreen>
                </PhonePreviewFrame>
            </PreviewColumn>
        </SplitModal>
    );
};

export default WhatsAppTemplateBuilder;
